import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import {
  searchModelConverter,
  SearchQueryTerm,
  SearchQueryTermWithoutPositions,
  searchRegexLanguageParser,
} from '@neptune/compound-search-domain';
import {
  type ComplexSearchFilter,
  CompoundSearchFilter,
  isEmptyFilter,
  persistFilter,
  TextFilter,
} from '@neptune/recent-searches-domain';
import { getCurrentRouteName, navigateTo } from '@neptune/shared/routing-business-logic';
import { AttributeNameFilter, NameSearchMode } from '@neptune/shared/search-domain';

import { fromTermsToCriterions } from './compound-search-term-utils';
import { useSearchQueryTermsState } from './use-search-query-terms-state';

export function useSearchFilters(namespace: string): {
  mode: NameSearchMode;
  setMode: (newMode: NameSearchMode, queryToPersist?: string) => void;
  isLoading: boolean;
  searchQueryTerms: SearchQueryTermWithoutPositions[];
  updateTermsFilter: (searchQueryTerms: SearchQueryTerm[]) => void;
  updateSingleTermFilter: (value: string) => void;
  toggleMode: (queryToPersist?: string) => void;
  attributeNameFilter: AttributeNameFilter | undefined;
} {
  const dispatch = useDispatch();
  const routeName = useSelector(getCurrentRouteName);

  const { compoundNamespace, loading, mode, searchQueryTerms } =
    useSearchQueryTermsState(namespace);

  const changeFilter = React.useCallback(
    async (newFilter?: ComplexSearchFilter): Promise<void> => {
      let newState = {};

      if (!newFilter) {
        newState = {
          [namespace]: undefined,
          [compoundNamespace]: undefined,
        };
      }

      if (newFilter?.searchType === NameSearchMode.REGEX) {
        const key = !isEmptyFilter(newFilter) ? await persistFilter(newFilter.value) : undefined;
        newState = {
          [namespace]: undefined,
          [compoundNamespace]: key,
        };
      }

      if (newFilter?.searchType === NameSearchMode.SUBSTRING) {
        newState = {
          [namespace]: newFilter.value,
          [compoundNamespace]: undefined,
        };
      }

      dispatch(navigateTo(routeName, newState, { extendParams: true, replace: true }));
    },
    [dispatch, routeName, namespace, compoundNamespace],
  );

  const updateSingleTermFilter = React.useCallback(
    (value: string) => {
      if (!value) {
        changeFilter(undefined);
        return;
      }

      const newFilter: TextFilter = {
        searchType: NameSearchMode.SUBSTRING,
        value,
      };

      changeFilter(newFilter);
    },
    [changeFilter],
  );

  const updateTermsFilter = React.useCallback(
    (newQueryTerms: SearchQueryTermWithoutPositions[]) => {
      if (newQueryTerms.length === 0) {
        changeFilter(undefined);
        return;
      }

      const adaptedValue: CompoundSearchFilter = {
        searchType: NameSearchMode.REGEX,
        value: fromTermsToCriterions(newQueryTerms),
      };

      changeFilter(adaptedValue);
    },
    [changeFilter],
  );

  const switchToSubStringMode = React.useCallback(
    (queryToPersist?: string) => {
      dispatch(
        navigateTo(
          routeName,
          {
            searchMode: NameSearchMode.SUBSTRING,
            [namespace]: queryToPersist,
            [compoundNamespace]: undefined,
          },
          { extendParams: true, replace: true },
        ),
      );
    },
    [dispatch, routeName, namespace, compoundNamespace],
  );

  const switchToRegexMode = React.useCallback(
    (queryToPersist?: string) => {
      dispatch(
        navigateTo(
          routeName,
          {
            searchMode: NameSearchMode.REGEX,
            [namespace]: undefined,
            [compoundNamespace]: undefined,
          },
          { extendParams: true, replace: true },
        ),
      );

      if (queryToPersist && queryToPersist.length > 0) {
        const { status, query } = searchRegexLanguageParser.parse(queryToPersist);

        if (status === 'invalid') {
          updateTermsFilter(searchQueryTerms);
          return;
        }

        updateTermsFilter(query);
      }
    },
    [dispatch, routeName, namespace, compoundNamespace, updateTermsFilter, searchQueryTerms],
  );

  const setMode = React.useCallback(
    (newMode: NameSearchMode, queryToPersist?: string) => {
      if (newMode === NameSearchMode.SUBSTRING) {
        switchToSubStringMode(queryToPersist);
        return;
      }

      switchToRegexMode(queryToPersist);
    },
    [switchToSubStringMode, switchToRegexMode],
  );

  const toggleMode = React.useCallback(
    (queryToPersist?: string) => {
      if (mode === NameSearchMode.REGEX) {
        setMode(NameSearchMode.SUBSTRING, queryToPersist);
        return;
      }

      setMode(NameSearchMode.REGEX, queryToPersist);
    },
    [mode, setMode],
  );

  /**
   * Placing this here for convenience, however, a refactor would be desirable since AttributeNameFilter should not be a
   * "domain" concern, but a "infrastructure" concern (it is used mainly by the API, and this DTO should not leak).
   */
  const attributeNameFilter: AttributeNameFilter | undefined = React.useMemo(() => {
    if (isEmpty(searchQueryTerms)) {
      return undefined;
    }

    if (mode === NameSearchMode.SUBSTRING) {
      return searchModelConverter.fromSearchQueryToSingleAttributeNameFilter(searchQueryTerms);
    }

    return searchModelConverter.fromSearchQueryToAttributeNameFilter(searchQueryTerms);
  }, [searchQueryTerms, mode]);

  return {
    mode,
    setMode,
    isLoading: loading,
    searchQueryTerms,
    updateTermsFilter,
    updateSingleTermFilter,
    toggleMode,
    attributeNameFilter,
  };
}
